import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Edit, Plus, Filter, Trash2, Edit2, Search, 
  Tag, Hash, Key, ScanLine, Shield, CircleDot, 
  Layers, Package, BadgeDollarSign, Clock, Image as ImageIcon,
  ArrowRightLeft, Pencil, X, ChevronLeft, ChevronRight, Loader
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { masterService } from "../../api/masterService";
import { useNotification } from "../../contexts/NotificationContext";

import EditProductModal from "./components/EditProductModal";
import EditVariantModal from "./components/EditVariantModal";
import LinkAddonGroupModal from "./components/LinkAddonGroupModal";

import ConfirmationModal from "../../components/ConfirmationModal";
import ImageCropperModal from "../../components/ImageCropperModal";
import ImageSourcePickerModal from "../../components/ImageSourcePickerModal";
import { getImageUrl } from "../../utils/imageUtils";


export default function ProductDetailApps() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: () => {},
  });

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);

  // Hanya tampilkan varian produk (tanpa brand) di Apps
  // Varian Merek tidak ditampilkan di Apps
  



  // Modal States
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [isEditVariantOpen, setIsEditVariantOpen] = useState(false);
  const [isAddBrandVariantOpen, setIsAddBrandVariantOpen] = useState(false);
  const [isEditingBrandVariant, setIsEditingBrandVariant] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isLinkAddonGroupOpen, setIsLinkAddonGroupOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productRes, variantsRes] = await Promise.all([
        masterService.products.get(id),
        masterService.products.variants.list(id)
      ]);
      setProduct(productRes.data);
      setVariants(variantsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      showError("Gagal memuat data produk");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const sidebarToggle = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  const handleEditVariant = (variant) => {
    setSelectedVariant(variant);
    setIsEditVariantOpen(true);
  };

  const handleAddVariant = () => {
    setSelectedVariant(null);
    setIsEditVariantOpen(true);
  };

  const handleLinkAddonGroup = () => {
    setIsLinkAddonGroupOpen(true);
  };

  const handleDeleteVariant = (variant) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Varian?",
      message: `Apakah Anda yakin ingin menghapus varian "${variant.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await masterService.products.variants.delete(id, variant.id);
          showSuccess("Varian berhasil dihapus");
          fetchData();
        } catch (err) {
          showError(err.message || "Gagal menghapus varian");
        } finally {
          setIsDeleting(false);
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleUnlinkAddonGroupConfirm = (group) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Kaitan Grup?",
      message: `Apakah Anda yakin ingin menghapus kaitan grup pilihan "${group.name}" dari produk ini?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          const newGroupIds = (product.addon_groups || [])
            .filter((g) => g.id !== group.id)
            .map((g) => g.id);
          await masterService.addonGroups.setProductAddonGroups(id, newGroupIds);
          showSuccess("Kaitan grup pilihan extra berhasil dihapus");
          fetchData();
        } catch (error) {
          showError("Gagal menghapus kaitan grup pilihan extra");
        } finally {
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };


  const [cropModal, setCropModal] = useState({
    isOpen: false,
    imageSrc: null,
    targetId: null,
    isPrimary: false
  });

  const [pickerModal, setPickerModal] = useState({
    isOpen: false,
    targetId: null,
    isPrimary: false
  });

  const triggerImagePicker = (targetId = null, isPrimary = false) => {
    setPickerModal({
      isOpen: true,
      targetId,
      isPrimary
    });
  };

  const handleSourceSelect = async (source) => {
    const { targetId, isPrimary } = pickerModal;

    if (source === "gallery") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/jpeg,image/png";
      input.onchange = (e) => handleImageSelect(e, targetId, isPrimary);
      input.click();
    } else if (source === "camera") {
      try {
        const { Camera, CameraResultType } = await import("@capacitor/camera");
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl
        });

        if (image && image.dataUrl) {
          setCropModal({
            isOpen: true,
            imageSrc: image.dataUrl,
            targetId,
            isPrimary
          });
        }
      } catch (err) {
        console.error("Camera capture error:", err);
        if (err.message && !err.message.includes("cancelled") && !err.message.includes("cancel")) {
          showError("Gagal membuka kamera. Pastikan izin kamera diberikan.");
        }
      }
    }
  };

  const handleImageSelect = (e, targetId = null, isPrimary = false) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showError("Ukuran gambar maksimal 5MB");
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setCropModal({
        isOpen: true,
        imageSrc: reader.result,
        targetId,
        isPrimary
      });
    };
    e.target.value = '';
  };

  const handleCropSave = async (base64Image) => {
    try {
      if (cropModal.targetId) {
        await masterService.products.deleteImage(id, cropModal.targetId);
      }
      await masterService.products.addImage(id, {
        image_base64: base64Image,
        sort_order: (product.images?.length || 0) + 1,
        is_primary: product.images?.length === 0 || cropModal.isPrimary
      });
      showSuccess("Gambar berhasil disimpan");
      fetchData();
    } catch (err) {
      showError(err.message || "Gagal menyimpan gambar");
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[#f8fafc]">
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
        <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-64"}`}>
          <Header />
          <div className="p-8 flex items-center justify-center min-h-[60vh] text-slate-500">
            <Loader className="w-6 h-6 animate-spin mr-3 text-teal-600" />
            <p className="font-medium text-lg">Memuat detail produk...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex h-screen bg-[#f8fafc]">
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
        <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-64"}`}>
          <Header />
          <div className="p-8 flex items-center justify-center min-h-[60vh]">
            <p>Produk tidak ditemukan</p>
          </div>
        </div>
      </div>
    );
  }

  const baseUnitId = product.unit_id;
  const baseUnitName = product.unit_name || "Pcs";

  // Split variants based on user requirements
  const productVariants = variants.filter(v => v.brand_id == null);
  const brandVariants = variants.filter(v => v.brand_id != null);

  const brandsList = brandVariants.length > 0 
    ? [...new Set(brandVariants.map(v => v.brand_name).filter(Boolean))].join(", ") 
    : "-";

  // Derived data: group variants by brand for the Varian Merek table
  const groupedBrandVariants = (() => {
    const map = new Map();
    brandVariants.forEach((v) => {
      const key = v.brand_id;
      if (!map.has(key)) {
        map.set(key, {
          brand_id: v.brand_id,
          brand_name: v.brand_name || "-",
          status: v.status,
          rows: [],
        });
      }
      const group = map.get(key);
      group.rows.push(v);
      if (v.status === "ACTIVE") group.status = "ACTIVE";
    });
    return Array.from(map.values());
  })();

  const getHargaDasar = (group, baseUnit, priceField) => {
    return group.rows.reduce((min, current) => {
      let currentPrice = current[priceField] || 0;
      let ratio = 1;
      
      const conv = conversions.find(c => 
        (c.from_unit_id === current.unit_id && c.to_unit_id === baseUnit) ||
        (c.from_unit_id === baseUnit && c.to_unit_id === current.unit_id)
      );

      if (conv) {
        if (conv.from_unit_id === current.unit_id) {
          ratio = conv.conversion_factor;
          currentPrice = currentPrice / ratio; 
        } else {
          ratio = conv.conversion_factor;
          currentPrice = currentPrice * ratio; 
        }
      }
      
      return Math.min(min, currentPrice);
    }, Infinity);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('id-ID', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };


  // Image Slot Logic — fallback to legacy image_url if images array is empty
  const rawImages = product.images && product.images.length > 0
    ? product.images
    : (product.image_url ? [{ id: null, image_url: product.image_url, is_primary: true, sort_order: 1 }] : []);
  const imagesArr = rawImages.slice().sort((a, b) => {
    if (a.is_primary) return -1;
    if (b.is_primary) return 1;
    return (a.sort_order || 99) - (b.sort_order || 99);
  });
  const mainImage = imagesArr[0];
  const thumbImages = imagesArr.slice(1, 5);

  const marginProduk = (product.sell_price || 0) - (product.purchase_price || 0);

  // Dynamic Pricing & Stock Calculation
  let displayPurchasePrice = product?.purchase_price || 0;
  let displaySellPrice = product?.sell_price || 0;
  if ((!product?.sell_price || product.sell_price === 0) && variants.length > 0) {
    displayPurchasePrice = variants[0].purchase_price || 0;
    displaySellPrice = variants[0].sell_price || 0;
  }

  const baseStock = product?.stok || 0;
  const variantsStock = variants.reduce((sum, v) => sum + (v.stok || 0), 0);
  // Jika ada varian: total stok dari varian. Jika tidak ada varian: pakai stok default produk (dari inventory)
  const calculatedTotalStock = variants.length > 0 ? variantsStock : baseStock;

  return (
    <div className="flex h-screen bg-[#f8fafc]">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
      <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-64"}`}>
        <Header />

        <div className="p-6 lg:p-8 w-full mx-auto space-y-6">
          
          {/* Header Section */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div className="flex items-start gap-4 min-w-0">
              <button 
                onClick={() => navigate("/master-data/produk")}
                className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-50 shrink-0 mt-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-[22px] font-bold text-gray-900 tracking-tight">Detail Produk</h1>
                <div className="flex items-center text-sm font-medium text-slate-400 mt-1">
                  <span className="hover:text-teal-600 cursor-pointer transition-colors shrink-0" onClick={() => navigate("/master-data/produk")}>Data Produk</span>
                  <ChevronRight className="w-4 h-4 mx-1.5 text-slate-300 shrink-0" />
                  <span className="text-slate-600 font-semibold truncate">{product.name}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
              <button
                className={`flex-1 sm:flex-none px-6 py-2.5 rounded-xl font-bold shadow-sm transition-colors text-center ${
                  product.status === "ACTIVE"
                    ? "bg-white border border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                    : "bg-rose-50 border border-rose-300 text-rose-600 hover:bg-rose-100"
                }`}>
                {product.status === "ACTIVE" ? "Aktif" : "Non-Aktif"}
              </button>
              <button onClick={() => setIsEditProductOpen(true)} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white border border-teal-500 rounded-xl text-teal-600 font-bold hover:bg-teal-50 shadow-sm transition-colors flex-1 sm:flex-none">
                <Edit className="w-4 h-4" /> Ubah
              </button>
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden flex flex-col md:flex-row p-6 lg:p-8 gap-8 lg:gap-12">
            {/* Left: Images */}
            <div className="w-full md:w-[350px] shrink-0 flex flex-col gap-4">
              <div className="aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden border border-slate-100 relative group flex items-center justify-center">
                {mainImage ? (
                  <>
                    <img src={getImageUrl(mainImage.image_url)} className="w-full h-full object-cover" alt="Product" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x300?text=Error'; }} />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 backdrop-blur-[2px]">
                      <button 
                        onClick={() => triggerImagePicker(mainImage.id, true)} 
                        className="px-4 py-2 bg-white rounded-lg font-bold text-sm text-slate-800 cursor-pointer w-28 text-center hover:bg-slate-50 transition-colors"
                      >
                        Ubah
                      </button>
                      {mainImage.id && (
                        <button 
                          onClick={() => masterService.products.deleteImage(id, mainImage.id).then(() => { showSuccess("Gambar dihapus"); fetchData(); })}
                          className="px-4 py-2 bg-rose-500 rounded-lg font-bold text-sm text-white cursor-pointer w-28 text-center hover:bg-rose-600 transition-colors">
                          Hapus
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <button 
                    onClick={() => triggerImagePicker(null, true)} 
                    className="w-full h-full flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-teal-500 hover:bg-teal-50 transition-colors"
                  >
                    <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                    <span className="text-sm font-semibold text-center leading-tight">Tambah Utama<br/><span className="text-[10px] font-normal text-slate-400">JPG/PNG (Maks 5MB)</span></span>
                  </button>
                )}
              </div>

              {/* HIDDEN FOR NOW: Single product photo requirement (kept for future use) */}
              {/*
              <div className="grid grid-cols-4 gap-3">
                {thumbImages.map((img, idx) => (
                  <div key={img.id} className="aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden border border-slate-200 relative group shadow-sm transition-all hover:border-teal-300">
                    <img src={getImageUrl(img.image_url)} className="w-full h-full object-cover" alt={`Thumb ${idx + 1}`} onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100?text=Error'; }} />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1.5 transition-opacity backdrop-blur-[1px]">
                      <button 
                        onClick={() => triggerImagePicker(img.id, false)} 
                        className="px-2 py-1 bg-white rounded-md font-semibold text-[10px] text-slate-800 cursor-pointer w-[80%] text-center hover:bg-slate-50 transition-colors shadow-sm"
                      >
                        Ubah
                      </button>
                      <button 
                        onClick={() => masterService.products.deleteImage(id, img.id).then(() => { showSuccess("Gambar dihapus"); fetchData(); })}
                        className="px-2 py-1 bg-rose-500 rounded-md font-semibold text-[10px] text-white cursor-pointer w-[80%] text-center hover:bg-rose-600 transition-colors shadow-sm">
                        Hapus
                      </button>
                    </div>
                  </div>
                ))}
                {imagesArr.length < 5 && (
                  <button 
                    onClick={() => triggerImagePicker(null, false)} 
                    className="aspect-[4/3] border border-dashed border-teal-300 text-teal-500 rounded-xl flex flex-col items-center justify-center hover:bg-teal-50 transition-colors relative overflow-hidden group cursor-pointer shadow-sm"
                  >
                    <Plus className="w-5 h-5 mb-0.5 group-hover:scale-110 transition-transform" strokeWidth={2.5} />
                    <span className="text-[10px] font-semibold text-center leading-tight">Tambah<br/><span className="text-[8px] font-normal text-slate-400">Maks 5MB</span></span>
                  </button>
                )}
              </div>
              */}
            </div>

            {/* Right: Info */}
            <div className="w-full flex-1">
              <h2 className="text-[24px] lg:text-[28px] font-extrabold text-gray-900 mb-8 uppercase tracking-wide">
                {product.name}
              </h2>
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-y-6 gap-x-12 text-[15px]">
                {/* Column 1 */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-400 shrink-0"><Tag className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Kategori</span>
                    <span className="text-[#1e293b] font-bold truncate">{product.category_name || "-"}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-400 shrink-0"><BadgeDollarSign className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Harga Modal</span>
                    <span className="text-[#1e293b] font-bold">{formatPrice(displayPurchasePrice)}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-400 shrink-0"><BadgeDollarSign className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Harga Jual</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[#1e293b] font-bold">{formatPrice(displaySellPrice)}</span>
                    </div>
                  </div>
                </div>

                {/* Column 2 */}
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-400 shrink-0"><Layers className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Stok</span>
                    <span className="text-[#1e293b] font-bold">{calculatedTotalStock}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 flex justify-center text-teal-400 shrink-0"><Package className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Satuan</span>
                    <span className="text-[#1e293b] font-bold">{product.unit_name || "-"}</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 flex justify-center text-teal-400 mt-0.5 shrink-0"><Clock className="w-5 h-5" /></div>
                    <span className="text-teal-600 font-medium w-28 sm:w-32 shrink-0">Update Terakhir</span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-gray-900 font-bold">{formatDate(product.updated_at)}</span>
                      <span className="text-teal-600 text-sm">{product.updated_by || "Sistem"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* HIDDEN TEMPORARILY: Deskripsi
              <hr className="my-8 border-slate-100" />
              
              <div>
                <h3 className="text-teal-600 font-medium mb-3">Deskripsi</h3>
                <p className="text-gray-900 text-sm leading-relaxed font-semibold">
                  {product.description || "-"}
                </p>
              </div>
              */}
            </div>
          </div>

          {/* HIDDEN TEMPORARILY: Varian Merek (di hide pada versi kuliner/apps)
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
              <h3 className="text-gray-900 text-[18px] font-bold">Varian Merek</h3>
              <button onClick={() => setIsAddBrandVariantOpen(true)} className="flex items-center justify-center gap-2 bg-white border border-teal-500 text-teal-600 rounded-xl hover:bg-teal-50 font-semibold text-sm transition-colors shadow-sm w-full sm:w-auto px-5 py-2">
                <Plus className="w-4 h-4" strokeWidth={2.5} /> Tambah Merek
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-12 text-center">No</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Merek</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Stok</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Harga Modal Dasar</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Harga Jual Dasar</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Satuan Dasar</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center w-24">Aksi</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 font-semibold">
                  {groupedBrandVariants.length > 0 ? (
                    groupedBrandVariants.map((group, idx) => {
                      const hargaModalDasar = getHargaDasar(group, baseUnitId, 'purchase_price');
                      const hargaDasar = getHargaDasar(group, baseUnitId, 'sell_price');
                      const primary = group.rows[0];
                      return (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-4 text-center text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-4">{group.brand_name || "-"}</td>
                        <td className="px-4 py-4 font-semibold text-teal-600">{primary.stok || "0"}</td>
                        <td className="px-4 py-4">
                           {formatCurrency(hargaModalDasar)} / <span className="text-slate-400 font-normal">{baseUnitName}</span>
                        </td>
                        <td className="px-4 py-4 text-teal-600">
                           {formatCurrency(hargaDasar)} / <span className="text-slate-400 font-normal">{baseUnitName}</span>
                        </td>
                        <td className="px-4 py-4 text-slate-600 font-medium">{baseUnitName}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => { setSelectedVariant(primary); setIsEditingBrandVariant(true); setIsEditVariantOpen(true); }} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-md transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDeleteVariant(primary, true)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )})
                  ) : (
                    <tr className="border-b border-slate-50">
                      <td colSpan="6" className="py-8 text-center">
                         <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
                           <Tag className="w-6 h-6 text-slate-400" />
                         </div>
                         <p className="text-slate-500 font-medium">Belum ada merek</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          */}

          {/* Varian Produk */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
              <h3 className="text-gray-900 text-[18px] font-bold">Varian Produk</h3>
              <button onClick={() => { setSelectedVariant(null); setIsEditingBrandVariant(false); setIsEditVariantOpen(true); }} className="flex items-center justify-center gap-2 px-5 py-2 border border-teal-500 text-teal-600 rounded-full hover:bg-teal-50 font-semibold text-sm transition-colors w-full sm:w-auto">
                <Plus className="w-4 h-4" strokeWidth={2.5} /> Tambah Varian
              </button>
            </div>
            
            <div className="overflow-x-auto border border-slate-100 rounded-2xl shadow-sm">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-12 text-center">No</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Varian</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Harga Modal</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Harga Jual</th>
                    {/* HIDDEN TEMPORARILY: Estimasi Waktu <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Estimasi Waktu</th> */}
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Stok</th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-28">Aksi</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 font-semibold">
                  {productVariants?.length > 0 ? (
                    productVariants.map((variant, idx) => {
                      const vMargin = (variant.sell_price || 0) - (variant.purchase_price || 0);
                      return (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-4 text-center text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-4">
                          {variant.name || "DEFAULT"}
                        </td>
                        <td className="px-4 py-4">{formatPrice(variant.purchase_price)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-teal-600">{formatPrice(variant.sell_price)}</span>
                          </div>
                        </td>
                        {/* HIDDEN TEMPORARILY: Estimasi Waktu <td className="px-4 py-4 text-slate-600">{variant.prep_time_mins ? `${variant.prep_time_mins} Menit` : "-"}</td> */}
                        <td className="px-4 py-4 text-gray-900 font-semibold">{variant.stok ?? 0} {variant.unit_name || product?.unit_name || ""}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => { setSelectedVariant(variant); setIsEditingBrandVariant(false); setIsEditVariantOpen(true); }} className="w-8 h-8 rounded-lg border border-emerald-500 text-emerald-500 flex items-center justify-center hover:bg-emerald-50 transition-colors">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteVariant(variant)} className="w-8 h-8 rounded-lg border border-rose-500 text-rose-500 flex items-center justify-center hover:bg-rose-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )})
                  ) : (
                    <tr className="border-b border-slate-100">
                      <td colSpan="7" className="px-4 py-4 text-center text-slate-500 font-medium">Belum ada varian produk</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pilihan Extra */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
              <h3 className="text-gray-900 text-[18px] font-bold">Pilihan Extra (Addon Groups)</h3>
              <button onClick={handleLinkAddonGroup} className="flex items-center justify-center gap-2 px-5 py-2 border border-teal-500 text-teal-600 rounded-full hover:bg-teal-50 font-semibold text-sm transition-colors shadow-sm w-full sm:w-auto">
                <Plus className="w-4 h-4" strokeWidth={2.5} /> Hubungkan Grup
              </button>
            </div>
            
            <div className="overflow-x-auto border border-slate-100 rounded-2xl shadow-sm">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-12 text-center">No</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Nama Grup</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Deskripsi</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Min/Max Pilihan</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Pilihan yang Tersedia</th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-28">Aksi</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 font-semibold">
                  {product.addon_groups?.length > 0 ? (
                    product.addon_groups.map((group, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-4 text-center text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-4">{group.name || "-"}</td>
                        <td className="px-4 py-4 text-slate-500">{group.description || "-"}</td>
                        <td className="px-4 py-4 text-slate-600">Min: {group.min_selection} / Max: {group.max_selection}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1 max-w-sm">
                            {(group.addons || []).map((a) => (
                              <span key={a.id} className="inline-flex px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600 font-medium border border-slate-200">
                                {a.name} (+Rp {a.sell_price.toLocaleString("id-ID")})
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => handleUnlinkAddonGroupConfirm(group)} className="w-8 h-8 rounded-lg border border-rose-500 text-rose-500 flex items-center justify-center hover:bg-rose-50 transition-colors" title="Hapus Kaitan">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-b border-slate-100">
                      <td colSpan="6" className="px-4 py-4 text-center text-slate-500 font-medium">Belum ada grup pilihan extra ditautkan</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
        </div>
      </div>
      
      <EditProductModal 
        isOpen={isEditProductOpen} 
        onClose={() => setIsEditProductOpen(false)} 
        product={product} 
        onSuccess={fetchData} 
      />
      <EditVariantModal
        isOpen={isEditVariantOpen}
        onClose={() => setIsEditVariantOpen(false)}
        variant={selectedVariant}
        productId={id}
        product={product}
        onSuccess={fetchData}
        isBrandVariant={isEditingBrandVariant}
        isRetail={false}
      />
      
      <ImageCropperModal
        isOpen={cropModal.isOpen}
        imageSrc={cropModal.imageSrc}
        onClose={() => setCropModal({ isOpen: false, imageSrc: null, targetId: null, isPrimary: false })}
        onSave={handleCropSave}
      />
      
      <ImageSourcePickerModal
        isOpen={pickerModal.isOpen}
        onClose={() => setPickerModal({ isOpen: false, targetId: null, isPrimary: false })}
        onSelect={handleSourceSelect}
      />

      <LinkAddonGroupModal
        isOpen={isLinkAddonGroupOpen}
        onClose={() => setIsLinkAddonGroupOpen(false)}
        productId={id}
        linkedGroupIds={(product.addon_groups || []).map((g) => g.id)}
        onSuccess={fetchData}
      />
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
        onConfirm={confirmationModal.onConfirm}
        isLoading={isDeleting}
        onCancel={() => setConfirmationModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

